import usePermissions from "@/hooks/use-permissions.hook";
import { ApiPermission } from "@/types/role.types";
import { EmployeeAvatarWithTooltip } from "@/components/employee/EmployeeAvatarWithTooltip";
import EmptyState from "@/components/empty-state/EmptyState";
import useFormatter from "@/hooks/use-formatter.hook";
import { useTitle } from "@/hooks/use-title.hook";
import { useHiringFlows, useHiringFlowArchive } from "@/queries/use-hiring-flows.query";
import { ApiHiringFlow } from "@/types/hiring-flow.types";
import { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  LinkTo,
  Page,
  Stack,
  Table,
  TableCell,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { HiringFlowCreateModal } from "@/pages/settings/hiring-flows/index/widgets/HiringFlowCreateModal";

export function HiringFlowsPage() {
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();
  const canHire = canIEdit(ApiPermission.add_employees);
  useTitle(t("hiring_flow:title"), t("common:settings"));
  const { data, isLoading, isFetching } = useHiringFlows();
  const archive = useHiringFlowArchive();
  const { openModal } = useModals();
  const { renderEmployeeName, renderDate } = useFormatter();
  const columns: ColumnDef<ApiHiringFlow>[] = [
    {
      accessorKey: "title",
      header: t("common:template"),
      cell: ({ row }) => (
        <TableCell>
          <LinkTo
            variant="plain"
            to={`/settings/hiring-flows/${row.original.id}`}
            className={"font-semibold"}
          >
            {row.original.title}
          </LinkTo>
        </TableCell>
      ),
    },
    {
      id: "owner",
      header: t("common:owner"),
      cell: ({ row }) => (
        <TableCell>
          {row.original.owner ? (
            <Stack direction="horizontal" items="center" gap="sm">
              <EmployeeAvatarWithTooltip employee={row.original.owner} size="sm" />
              {renderEmployeeName(row.original.owner)}
            </Stack>
          ) : (
            "—"
          )}
        </TableCell>
      ),
    },
    {
      accessorKey: "datetime_updated_at",
      header: t("hiring_flow:last_updated"),
      cell: ({ row }) => <Text>{renderDate(row.original.datetime_updated_at)}</Text>,
    },
  ];
  const archiveFlow = (flow: ApiHiringFlow) =>
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("hiring_flow:archive_confirm", { title: flow.title }),
      acceptTitle: t("common:archive"),
      onAccept: async () => {
        try {
          await archive.mutateAsync(flow.id);
        } catch {
          return;
        }
      },
    });
  return (
    <Page>
      <Page.Header title={t("hiring_flow:title")} showBack navigateTo="/settings/employment">
        <Button onClick={() => openModal(HiringFlowCreateModal, {})}>
          {t("common:new_template")}
        </Button>
      </Page.Header>
      <Page.Content>
        <Stack className={"px-16"}>
          <Table
            data={data ?? []}
            columns={columns}
            showSkeleton={isLoading}
            isFetching={isFetching || archive.isPending}
            emptyStateComponent={<EmptyState />}
            renderRowAction={(row) =>
              canHire || !row.original.is_default ? (
                <DropdownMenu>
                  {canHire && (
                    <DropdownMenuItem
                      title={t("hiring_flow:new_employee_from_this")}
                      to={`/employees/hire?hiring_flow_id=${row.original.id}`}
                    />
                  )}
                  {!row.original.is_default && (
                    <DropdownMenuItem
                      title={t("common:archive")}
                      variant="menuDanger"
                      onClick={() => archiveFlow(row.original)}
                    />
                  )}
                </DropdownMenu>
              ) : null
            }
          />
        </Stack>
      </Page.Content>
    </Page>
  );
}
